// Money formatting for Cache value surfaces (GET-266).
//
// Owner rule 2026-11-05: the new value components never show cents. Amounts
// are rounded to whole units and thousands-separated. Existing screens keep
// their own `toFixed(2)` formatting until their redesign tickets.

const DEFAULT_CURRENCY = 'USD';
const MINUS_SIGN = '−';

function wholeUnits(amount: number): number {
  if (!Number.isFinite(amount)) {
    throw new RangeError(`formatCacheValue: amount must be finite, got ${amount}`);
  }
  const rounded = Math.round(amount);
  // Math.round(-0.4) is -0, which Intl renders as "-$0".
  return rounded === 0 ? 0 : rounded;
}

/** "XYZ 1,234": the code as a prefix, for codes Intl has no symbol for. */
function codePrefixed(rounded: number, code: string): string {
  const digits = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(
    Math.abs(rounded)
  );
  return `${rounded < 0 ? '-' : ''}${code} ${digits}`;
}

/** Whole-unit, thousands-separated money for Cache value surfaces.
 *  Owner rule 2026-11-05: never cents on the new components.
 *
 *  formatCacheValue(1685)        → "$1,685"
 *  formatCacheValue(419.5)       → "$420"
 *  formatCacheValue(1234, 'GBP') → "£1,234"
 *  formatCacheValue(1234, 'XYZ') → "XYZ 1,234"
 *  formatCacheValue(NaN)         → throws RangeError (render the unvalued state instead)
 */
export function formatCacheValue(amount: number, currency?: string | null): string {
  const rounded = wholeUnits(amount);
  const code = (currency ?? '').trim().toUpperCase() || DEFAULT_CURRENCY;
  try {
    const formatted = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: code,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(rounded);
    // A well-formed code with no known symbol (e.g. "XYZ") formats as
    // "XYZ 1,234". Normalise it to the plain-space fallback form.
    return formatted.includes(code) ? codePrefixed(rounded, code) : formatted;
  } catch {
    // A malformed code (not three letters) makes the constructor throw.
    return codePrefixed(rounded, code);
  }
}

/** Signed change for deltas: "+$5", "−$12" (U+2212 minus), "$0". */
export function formatCacheDelta(amount: number, currency?: string | null): string {
  const rounded = wholeUnits(amount);
  const sign = rounded > 0 ? '+' : rounded < 0 ? MINUS_SIGN : '';
  return `${sign}${formatCacheValue(Math.abs(rounded), currency)}`;
}
